import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
  Building2, Palette, Crown, Gem, Bell, FileText, Tag, MapPin, Calendar, Key, Shield, Save, Plus, X, PhoneCall, ShieldCheck, Bug,
} from 'lucide-react';
import { toast } from 'sonner';
import WarrantyCompaniesForm from './WarrantyCompaniesForm';
import DiagnosticsErrorsForm from './DiagnosticsErrorsForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

// ====== helper sub-components ======
const Section = ({ children, title, description }) => (
  <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
    <div>
      <h3 className="text-white font-medium">{title}</h3>
      {description && <p className="text-[#A3A3A3] text-sm mt-1">{description}</p>}
    </div>
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-white">{label}</Label>
    {children}
    {hint && <p className="text-[#525252] text-xs mt-1">{hint}</p>}
  </div>
);

const SaveBar = ({ onSave, saving, dirty }) => (
  <div className="sticky bottom-4 flex justify-end">
    <Button
      data-testid="settings-save"
      onClick={onSave}
      disabled={saving || !dirty}
      className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] shadow-lg"
    >
      <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
    </Button>
  </div>
);

// ====== Section renderers ======
const CompanyForm = ({ value, onChange }) => (
  <Section title="Company Information" description="Shown on invoices, emails, and the public site.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Company Name"><Input data-testid="company-name" value={value.name || ''} onChange={(e) => onChange({ ...value, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Tagline"><Input value={value.tagline || ''} onChange={(e) => onChange({ ...value, tagline: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Phone"><Input value={value.phone || ''} onChange={(e) => onChange({ ...value, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Email"><Input type="email" value={value.email || ''} onChange={(e) => onChange({ ...value, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Website"><Input value={value.website || ''} onChange={(e) => onChange({ ...value, website: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="License #"><Input value={value.license_number || ''} onChange={(e) => onChange({ ...value, license_number: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="EIN"><Input value={value.ein || ''} onChange={(e) => onChange({ ...value, ein: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Timezone" hint="IANA timezone, e.g. America/Detroit"><Input value={value.timezone || ''} onChange={(e) => onChange({ ...value, timezone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Address Line 1"><Input value={value.address_line1 || ''} onChange={(e) => onChange({ ...value, address_line1: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Address Line 2"><Input value={value.address_line2 || ''} onChange={(e) => onChange({ ...value, address_line2: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="City"><Input value={value.city || ''} onChange={(e) => onChange({ ...value, city: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="State"><Input value={value.state || ''} onChange={(e) => onChange({ ...value, state: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="ZIP"><Input value={value.zip || ''} onChange={(e) => onChange({ ...value, zip: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
    </div>
    {/* iter78 — Public App URL: used in payment links, QR codes, password-reset emails, portal
        invites, and Stripe redirect URLs. CRITICAL: must be the customer-facing domain
        (https://hub.nexflowplumbing.com), NOT an internal cluster hostname. Editable here so
        deployment domain changes never require a code redeploy. */}
    <div className="grid sm:grid-cols-1 gap-4">
      <Field
        label="Public App URL (production domain)"
        hint="Must be the customer-facing URL, e.g. https://hub.nexflowplumbing.com. Used by payment links, QR codes, password reset emails, and portal invites. If left blank, the system falls back to the request host — which on production may leak the internal deployment hostname."
      >
        <Input
          data-testid="company-public-app-url"
          value={value.public_app_url || ''}
          onChange={(e) => onChange({ ...value, public_app_url: e.target.value.trim() })}
          placeholder="https://hub.nexflowplumbing.com"
          className="bg-[#0A0A0A] border-[#262626] text-white"
        />
      </Field>
    </div>
  </Section>
);

const BrandingForm = ({ value, onChange }) => (
  <Section title="Branding & Logo" description="Used across the app, emails, and the login screen.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="App Display Name"><Input value={value.app_display_name || ''} onChange={(e) => onChange({ ...value, app_display_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Logo URL" hint="HTTPS URL of the logo image"><Input value={value.logo_url || ''} onChange={(e) => onChange({ ...value, logo_url: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Favicon URL"><Input value={value.favicon_url || ''} onChange={(e) => onChange({ ...value, favicon_url: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
    </div>
    <div className="grid sm:grid-cols-4 gap-4">
      {['primary_color', 'accent_color', 'background_color', 'text_color'].map((k) => (
        <Field key={k} label={k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}>
          <div className="flex items-center gap-2">
            <input type="color" value={value[k] || '#000000'} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="w-10 h-10 rounded border border-[#262626] bg-transparent cursor-pointer" />
            <Input value={value[k] || ''} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-sm" />
          </div>
        </Field>
      ))}
    </div>
    {value.logo_url && (
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 inline-block">
        <p className="text-[#525252] text-xs mb-2">Preview</p>
        <img src={value.logo_url} alt="logo preview" className="h-16 w-auto" />
      </div>
    )}
  </Section>
);

const MembershipForm = ({ value, onChange }) => {
  const tiers = value.tiers || [];
  const updateTier = (idx, patch) => onChange({ ...value, tiers: tiers.map((t, i) => i === idx ? { ...t, ...patch } : t) });
  const addTier = () => onChange({ ...value, tiers: [...tiers, {
    _uid: uid(), key: `tier_${tiers.length+1}`, label: 'New Tier',
    display_name: 'New Plan', subtitle: '', description: '',
    annual_price: 0, monthly_price: 0, allow_monthly: false,
    badge: '', display_order: tiers.length + 1, discount_percent: 0,
    active: true, perks: [],
  }] });
  const removeTier = (idx) => onChange({ ...value, tiers: tiers.filter((_, i) => i !== idx) });
  return (
    <Section title="Membership Plans" description="Single source of truth for the customer portal cards, the Pay Annually / Pay Monthly toggle, and estimate/invoice membership line picks.">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Currency"><Input value={value.currency || 'USD'} onChange={(e) => onChange({ ...value, currency: e.target.value.toUpperCase() })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
        <div className="flex items-center gap-3 pt-6">
          <Switch checked={!!value.auto_renew_default} onCheckedChange={(v) => onChange({ ...value, auto_renew_default: v })} data-testid="auto-renew-default" />
          <span className="text-white text-sm">Default to auto-renew on new memberships</span>
        </div>
      </div>
      <div className="space-y-3">
        {tiers.map((t, idx) => (
          <div key={t._uid || t.key || `tier-${idx}`} className="border border-[#262626] rounded-md p-4 space-y-3" data-testid={`settings-tier-${t.key || idx}`}>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Key (slug)"><Input value={t.key || ''} onChange={(e) => updateTier(idx, { key: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
              <Field label="Internal Label"><Input value={t.label || ''} onChange={(e) => updateTier(idx, { label: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
              <Field label="Public Plan Name"><Input value={t.display_name || t.label || ''} onChange={(e) => updateTier(idx, { display_name: e.target.value })} placeholder="Protect Plan" className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Subtitle"><Input value={t.subtitle || ''} onChange={(e) => updateTier(idx, { subtitle: e.target.value })} placeholder="Our Most Popular Plan" className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
              <Field label="Badge">
                <select
                  value={t.badge || ''}
                  onChange={(e) => updateTier(idx, { badge: e.target.value })}
                  className="bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-3 w-full"
                  data-testid={`tier-badge-${t.key || idx}`}
                >
                  <option value="">— None —</option>
                  <option value="MOST POPULAR">Most Popular</option>
                  <option value="BEST VALUE">Best Value</option>
                </select>
              </Field>
              <Field label="Display Order"><Input type="number" min={0} value={t.display_order ?? 0} onChange={(e) => updateTier(idx, { display_order: parseInt(e.target.value || '0', 10) })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
            </div>
            <Field label="Description">
              <Textarea rows={2} value={t.description || ''} onChange={(e) => updateTier(idx, { description: e.target.value })} placeholder="Short marketing paragraph shown on the card." className="bg-[#0A0A0A] border-[#262626] text-white" />
            </Field>
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <Field label="Annual Price ($/yr)"><Input data-testid={`tier-annual-${t.key || idx}`} type="number" min={0} value={t.annual_price ?? 0} onChange={(e) => updateTier(idx, { annual_price: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
              <Field label="Monthly Price ($/mo)"><Input data-testid={`tier-monthly-${t.key || idx}`} type="number" min={0} value={t.monthly_price ?? 0} onChange={(e) => updateTier(idx, { monthly_price: parseFloat(e.target.value || '0') })} disabled={!t.allow_monthly} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-50" /></Field>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={!!t.allow_monthly} onCheckedChange={(v) => updateTier(idx, { allow_monthly: v })} data-testid={`tier-allow-monthly-${t.key || idx}`} />
                <span className="text-white text-sm">Allow Monthly</span>
              </div>
              <Field label="Discount %"><Input type="number" min={0} max={100} value={t.discount_percent ?? 0} onChange={(e) => updateTier(idx, { discount_percent: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
            </div>
            <Field label="Perks (one per line — shown as bullet list on the customer card)">
              <Textarea
                data-testid={`tier-perks-${t.key || idx}`}
                rows={6}
                value={(t.perks || []).join('\n')}
                // iter153 — Trim each line on save but keep blank lines while the
                // admin is mid-edit so newlines (incl. Tab-inserted ones) survive
                // through controlled-value re-renders. Server `_normalize_plan`
                // filters blanks before sending plans to the customer portal.
                onChange={(e) => updateTier(idx, { perks: e.target.value.split('\n').map((s) => s.trim()) })}
                onKeyDown={(e) => {
                  // Tab inside the Perks textarea inserts a newline so the admin can
                  // rapid-type "perk 1 → Tab → perk 2 → Tab → perk 3" without focus
                  // jumping to the next form field. Shift+Tab still navigates backward.
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    const ta = e.target;
                    const start = ta.selectionStart;
                    const end = ta.selectionEnd;
                    const next = `${ta.value.slice(0, start)}\n${ta.value.slice(end)}`;
                    updateTier(idx, { perks: next.split('\n').map((s) => s.trim()) });
                    requestAnimationFrame(() => {
                      try {
                        ta.selectionStart = ta.selectionEnd = start + 1;
                      } catch { /* noop */ }
                    });
                  }
                }}
                className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-sm"
                placeholder={"Annual visual plumbing inspection\nSump pump check\nBasic leak inspection\n10% OFF plumbing repairs"}
              />
            </Field>
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Switch checked={t.active !== false} onCheckedChange={(v) => updateTier(idx, { active: v })} data-testid={`tier-active-${t.key || idx}`} />
                <span className="text-white text-sm">Active (visible to customers)</span>
              </div>
              <Button type="button" variant="outline" onClick={() => removeTier(idx)} className="border-red-700 text-red-400 hover:bg-red-900/30"><X className="w-3 h-3 mr-1" /> Remove Plan</Button>
            </div>
            {/* iter151 — Sanity hint: monthly × 12 should be ≥ annual or it's a worse deal for the customer. */}
            {t.allow_monthly && Number(t.monthly_price) > 0 && Number(t.monthly_price) * 12 < Number(t.annual_price) && (
              <p className="text-xs text-amber-400">
                ⚠ Monthly × 12 (${(Number(t.monthly_price) * 12).toFixed(2)}) is cheaper than annual (${Number(t.annual_price).toFixed(2)}). Annual should be the better deal.
              </p>
            )}
          </div>
        ))}
        <Button type="button" onClick={addTier} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Plus className="w-4 h-4 mr-2" /> Add Plan</Button>
      </div>
    </Section>
  );
};

const VipPayoutsForm = ({ value, onChange }) => (
  <Section title="VIP Referral Payouts" description="Defaults for the VIP referral program. Per-VIP overrides remain in /admin/vip-settings.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Default Referral Bonus ($)"><Input type="number" min={0} value={value.referral_bonus_amount} onChange={(e) => onChange({ ...value, referral_bonus_amount: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Minimum Payout Threshold ($)"><Input type="number" min={0} value={value.min_payout_threshold} onChange={(e) => onChange({ ...value, min_payout_threshold: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Default Payout Method"><Input value={value.payout_method_default || ''} onChange={(e) => onChange({ ...value, payout_method_default: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="check / ach / venmo" /></Field>
      <div className="flex items-center gap-3 pt-6">
        <Switch checked={!!value.auto_payout_enabled} onCheckedChange={(v) => onChange({ ...value, auto_payout_enabled: v })} data-testid="auto-payout" />
        <span className="text-white text-sm">Auto-payout when threshold reached</span>
      </div>
    </div>
  </Section>
);

const SystemNotificationsForm = ({ value, onChange }) => {
  const updateEvent = (key, ch, on) => onChange({
    ...value,
    events: { ...(value.events || {}), [key]: { ...(value.events?.[key] || {}), [ch]: on } },
  });
  const eventList = Object.keys(value.events || {});
  return (
    <Section title="System Notifications" description="Which admins get pinged for which events (separate from each user's personal preferences).">
      <Field label="Admin Recipients (comma-separated emails)">
        <Input
          data-testid="admin-recipients"
          value={(value.admin_recipients || []).join(', ')}
          onChange={(e) => onChange({ ...value, admin_recipients: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          className="bg-[#0A0A0A] border-[#262626] text-white"
          placeholder="ops@nexflow.com, dispatch@nexflow.com"
        />
      </Field>
      <div className="space-y-2">
        {eventList.map((k) => {
          const cfg = value.events[k] || {};
          return (
            <div key={k} className="flex items-center justify-between p-3 rounded-md hover:bg-[#1a1a1a]">
              <p className="text-white text-sm capitalize">{k.replace(/_/g, ' ')}</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                  <Switch checked={!!cfg.email} onCheckedChange={(v) => updateEvent(k, 'email', v)} /> Email
                </label>
                <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                  <Switch checked={!!cfg.in_app} onCheckedChange={(v) => updateEvent(k, 'in_app', v)} /> In-App
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const InvoiceDefaultsForm = ({ value, onChange }) => (
  <Section title="Invoice & Estimate Defaults" description="Applied to all new invoices and estimates.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Tax Rate %"><Input type="number" min={0} step={0.01} value={value.tax_rate_percent} onChange={(e) => onChange({ ...value, tax_rate_percent: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Default Due Days"><Input type="number" min={0} value={value.default_due_days} onChange={(e) => onChange({ ...value, default_due_days: parseInt(e.target.value || '0', 10) })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Estimate Validity Days"><Input type="number" min={1} value={value.estimate_validity_days} onChange={(e) => onChange({ ...value, estimate_validity_days: parseInt(e.target.value || '0', 10) })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Payment Terms"><Input value={value.payment_terms || ''} onChange={(e) => onChange({ ...value, payment_terms: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Invoice Number Prefix"><Input value={value.invoice_number_prefix || ''} onChange={(e) => onChange({ ...value, invoice_number_prefix: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Estimate Number Prefix"><Input value={value.estimate_number_prefix || ''} onChange={(e) => onChange({ ...value, estimate_number_prefix: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
    </div>
    <Field label="Footer Text"><Textarea rows={3} value={value.footer_text || ''} onChange={(e) => onChange({ ...value, footer_text: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
    <Field label="Accepted Payment Methods (comma-separated)" hint="e.g. card, ach, check, cash">
      <Input value={(value.accepted_payment_methods || []).join(', ')} onChange={(e) => onChange({ ...value, accepted_payment_methods: e.target.value.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean) })} className="bg-[#0A0A0A] border-[#262626] text-white" />
    </Field>
    <div className="flex items-center gap-3">
      <Switch checked={!!value.show_license_on_invoice} onCheckedChange={(v) => onChange({ ...value, show_license_on_invoice: v })} />
      <span className="text-white text-sm">Show company license on invoices</span>
    </div>
  </Section>
);

const PromoReferralForm = ({ value, onChange }) => (
  <Section title="Promo & Referral" description="Defaults for promo codes and the referral program.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="VIP Default Discount %"><Input type="number" min={0} max={100} value={value.vip_default_discount_percent} onChange={(e) => onChange({ ...value, vip_default_discount_percent: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="VIP Default Referral $"><Input type="number" min={0} value={value.vip_default_referral_amount} onChange={(e) => onChange({ ...value, vip_default_referral_amount: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Promo Max Discount %"><Input type="number" min={0} max={100} value={value.promo_max_discount_percent} onChange={(e) => onChange({ ...value, promo_max_discount_percent: parseFloat(e.target.value || '0') })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <div className="flex items-center gap-3 pt-6">
        <Switch checked={!!value.allow_promo_stacking} onCheckedChange={(v) => onChange({ ...value, allow_promo_stacking: v })} />
        <span className="text-white text-sm">Allow stacking promo codes</span>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!value.auto_apply_eligible_promos} onCheckedChange={(v) => onChange({ ...value, auto_apply_eligible_promos: v })} />
        <span className="text-white text-sm">Auto-apply eligible promos at checkout</span>
      </div>
    </div>
  </Section>
);

const DispatchForm = ({ value, onChange }) => (
  <Section title="Dispatch & Service Area" description="Routing, coverage and emergency settings.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Primary ZIP Codes (comma-separated)">
        <Input
          data-testid="dispatch-zips"
          value={(value.primary_zip_codes || []).join(', ')}
          onChange={(e) => onChange({ ...value, primary_zip_codes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          className="bg-[#0A0A0A] border-[#262626] text-white"
        />
      </Field>
      <Field label="Max Service Radius (miles)"><Input type="number" min={0} value={value.max_service_radius_miles} onChange={(e) => onChange({ ...value, max_service_radius_miles: parseInt(e.target.value || '0', 10) })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Headquarters Address"><Input value={value.headquarters_address || ''} onChange={(e) => onChange({ ...value, headquarters_address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Dispatch Email"><Input type="email" value={value.dispatch_email || ''} onChange={(e) => onChange({ ...value, dispatch_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Dispatch Phone"><Input value={value.dispatch_phone || ''} onChange={(e) => onChange({ ...value, dispatch_phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <div className="flex items-center gap-3 pt-6">
        <Switch checked={!!value.auto_assign_by_department} onCheckedChange={(v) => onChange({ ...value, auto_assign_by_department: v })} />
        <span className="text-white text-sm">Auto-assign jobs by department</span>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!value.emergency_after_hours} onCheckedChange={(v) => onChange({ ...value, emergency_after_hours: v })} />
        <span className="text-white text-sm">Accept emergency after-hours calls</span>
      </div>
    </div>
  </Section>
);

const HolidayCalendarForm = ({ value, onChange }) => {
  const paid = value.paid_holidays || [];
  const closed = value.office_closed_dates || [];
  const updatePaid = (idx, patch) => onChange({ ...value, paid_holidays: paid.map((h, i) => i === idx ? { ...h, ...patch } : h) });
  const addPaid = () => onChange({ ...value, paid_holidays: [...paid, { _uid: uid(), name: '', date: '' }] });
  const removePaid = (idx) => onChange({ ...value, paid_holidays: paid.filter((_, i) => i !== idx) });
  return (
    <Section title="Holiday Calendar" description="Paid holidays drive payroll boosts; office-closed dates block scheduling.">
      <div>
        <p className="text-white text-sm mb-2">Paid Holidays</p>
        <div className="space-y-2">
          {paid.map((h, idx) => (
            <div key={h._uid || `holiday-${idx}`} className="grid sm:grid-cols-3 gap-2 items-end">
              <Field label={`Name #${idx + 1}`}><Input value={h.name} onChange={(e) => updatePaid(idx, { name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
              <Field label="Date"><Input type="date" value={h.date || ''} onChange={(e) => updatePaid(idx, { date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
              <Button type="button" variant="outline" onClick={() => removePaid(idx)} className="border-red-700 text-red-400 hover:bg-red-900/30"><X className="w-3 h-3 mr-1" /> Remove</Button>
            </div>
          ))}
          <Button type="button" onClick={addPaid} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Plus className="w-4 h-4 mr-2" /> Add Holiday</Button>
        </div>
      </div>
      <Field label="Office Closed Dates (comma-separated YYYY-MM-DD)" hint="Use sparingly — these block all scheduling on those days.">
        <Input value={closed.join(', ')} onChange={(e) => onChange({ ...value, office_closed_dates: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className="bg-[#0A0A0A] border-[#262626] text-white" />
      </Field>
    </Section>
  );
};

// iter90 — Email Test Panel embedded inside the Twilio Email card. Lets the Super Admin
// fire a real test email and see structured success/failure WITHOUT having to trigger a
// real invoice or portal invite.
const EmailTestPanel = ({ currentFrom }) => {
  const { user } = useAuth();
  const [to, setTo] = useState(user?.email || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // iter94 typo guard
  const [typoSuggestion, setTypoSuggestion] = useState(null);

  React.useEffect(() => { if (user?.email && !to) setTo(user.email); /* eslint-disable-next-line */ }, [user?.email]);

  const send = async ({ forceSend = false, overrideTo = null } = {}) => {
    const recipient = (overrideTo || to || '').trim();
    if (!recipient || !recipient.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    setSending(true);
    setResult(null);
    setTypoSuggestion(null);
    try {
      const { data } = await axios.post(`${API}/admin/diagnostics/email-test`,
        { to_email: recipient, force_send: !!forceSend }, { withCredentials: true });
      if (data?.code === 'EMAIL_TYPO_SUSPECTED') {
        setTypoSuggestion({ to: data.to, suggestion: data.suggestion, reason: data.reason });
        setSending(false);
        return;
      }
      setResult(data);
      if (data?.sent) {
        toast.success(`Test email sent to ${recipient}`);
      } else {
        toast.error(data?.error || 'Test email failed — see details below');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Test email failed';
      setResult({ sent: false, error: msg });
      toast.error(msg);
    }
    setSending(false);
  };

  return (
    <div data-testid="email-test-panel" className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-md p-3 space-y-2">
      <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em]">SEND TEST EMAIL</p>
      <p className="text-[#A3A3A3] text-xs">Fires a real email using the saved API key + From Email. Save the form first if you just pasted a new key.</p>
      <div className="flex gap-2 flex-wrap">
        <input
          data-testid="email-test-to"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="recipient@example.com"
          className="flex-1 min-w-[200px] bg-[#141414] border border-[#262626] rounded px-2.5 py-1.5 text-sm text-white placeholder-[#525252] focus:outline-none focus:border-[#D4AF37]"
        />
        <Button
          data-testid="email-test-send"
          onClick={() => send()}
          disabled={sending}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs"
        >
          {sending ? 'Sending…' : 'Send Test Email'}
        </Button>
      </div>
      {currentFrom && (
        <p className="text-[10px] text-[#525252]">From: <span className="text-[#A3A3A3]">{currentFrom}</span></p>
      )}
      {typoSuggestion && (
        <div data-testid="email-test-typo-warning"
             className="rounded-md border border-[#D4AF37] bg-[#D4AF37]/10 p-3 text-xs space-y-2">
          <p className="font-bold text-[#D4AF37]">Did you mean <span className="font-mono">{typoSuggestion.suggestion}</span>?</p>
          <p className="text-[#A3A3A3]">{typoSuggestion.reason}</p>
          <p className="text-[#A3A3A3]">SendGrid would accept <span className="font-mono text-white">{typoSuggestion.to}</span> at the API level then silently bounce on delivery.</p>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="email-test-use-suggestion"
              onClick={() => {
                const corrected = typoSuggestion.suggestion;
                setTo(corrected);
                setTypoSuggestion(null);
                send({ forceSend: true, overrideTo: corrected });
              }}
              className="h-8 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs font-bold"
            >
              Use Suggestion
            </Button>
            <Button
              data-testid="email-test-send-anyway"
              onClick={() => send({ forceSend: true })}
              variant="outline"
              className="h-8 border-[#7F1D1D] text-red-300 hover:bg-[#7F1D1D]/20 text-xs"
            >
              Send Anyway
            </Button>
            <Button
              onClick={() => setTypoSuggestion(null)}
              variant="outline"
              className="h-8 border-[#262626] text-white hover:bg-[#262626] text-xs"
            >
              Edit Address
            </Button>
          </div>
        </div>
      )}
      {result && (
        <div
          data-testid="email-test-result"
          className={`mt-2 rounded-md border p-3 text-xs ${result.sent ? 'bg-green-500/10 border-green-700 text-green-300' : 'bg-[#7F1D1D]/15 border-[#7F1D1D] text-red-300'}`}
        >
          {result.sent ? (
            <>
              <p className="font-bold mb-1">✓ Sent successfully</p>
              <p>Status: <span className="font-mono">{result.status}</span></p>
              {result.message_id && <p>Message ID: <span className="font-mono break-all">{result.message_id}</span></p>}
              <p>From: <span className="font-mono">{result.from_email}</span></p>
              <p>To: <span className="font-mono">{result.to}</span></p>
              <p className="mt-1 text-[#A3A3A3]">Check the recipient inbox (and spam folder) in the next minute.</p>
            </>
          ) : (
            <>
              <p className="font-bold mb-1">✗ Test email failed</p>
              <p className="break-words whitespace-pre-wrap">{result.error || 'Unknown error'}</p>
              {result.configured && (
                <div className="mt-2 pt-2 border-t border-[#7F1D1D]/40 space-y-0.5 text-[#A3A3A3]">
                  <p>API key source: <span className="font-mono">{result.configured.api_key_source}</span></p>
                  <p>From email source: <span className="font-mono">{result.configured.from_email_source}</span></p>
                  <p>From email: <span className="font-mono">{result.configured.from_email || '—'}</span></p>
                </div>
              )}
              <ul className="mt-2 list-disc pl-4 text-[#A3A3A3] text-[11px] space-y-0.5">
                <li>Make sure you SAVED the API key + From Email above before testing.</li>
                <li>From Email must be a verified Single Sender OR a domain you authenticated in SendGrid.</li>
                <li>If status=401, the API key is invalid or revoked.</li>
                <li>If status=403, the From Email is not verified yet.</li>
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// iter99 — Stripe live/test mode banner. Reads /api/admin/integrations/stripe-status
// which reports what the running backend container is actually using (env-var only).
const StripeModeBanner = ({ status, loading, onRefresh }) => {
  // iter101 — Ground-truth probe. Calls /api/admin/integrations/stripe-test-checkout
  // which creates a real Stripe session and returns its id prefix (cs_live_/cs_test_)
  // so admins can definitively confirm what mode the running container is in.
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const runProbe = async () => {
    setProbing(true);
    setProbe(null);
    try {
      const { data } = await axios.post(`${API}/admin/integrations/stripe-test-checkout`, {}, { withCredentials: true });
      setProbe(data);
    } catch (err) {
      setProbe({ error: err.response?.data?.detail || err.message || 'Probe failed' });
    }
    setProbing(false);
  };

  if (loading && !status) {
    return (
      <div data-testid="stripe-mode-banner-loading" className="mb-3 px-3 py-2 rounded-md border border-[#262626] bg-[#0A0A0A] text-[#A3A3A3] text-xs">
        Checking Stripe mode…
      </div>
    );
  }
  if (!status) return null;

  const mode = status.mode || 'unknown';
  const isLive = mode === 'live';
  const isTest = mode === 'test';
  const isMissing = mode === 'unconfigured';

  const palette = isLive
    ? { border: 'border-green-700',  bg: 'bg-green-500/10',  text: 'text-green-400',  label: 'Live Mode' }
    : isTest
    ? { border: 'border-amber-700',  bg: 'bg-amber-500/10',  text: 'text-amber-400',  label: 'Test Mode' }
    : isMissing
    ? { border: 'border-red-700',    bg: 'bg-red-500/10',    text: 'text-red-400',    label: 'Not Configured' }
    : { border: 'border-[#262626]',  bg: 'bg-[#1A1A1A]',     text: 'text-[#A3A3A3]',  label: 'Unknown' };

  return (
    <div
      data-testid="stripe-mode-banner"
      className={`mb-3 rounded-md border ${palette.border} ${palette.bg} p-3 space-y-1.5`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span data-testid={`stripe-mode-${mode}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${palette.border} ${palette.text}`}>
            Stripe: {palette.label}
          </span>
          {status.key_prefix && (
            <span data-testid="stripe-key-prefix" className="text-[#A3A3A3] font-mono text-[11px]">
              key prefix <span className="text-white">{status.key_prefix}…</span> · {status.key_length} chars
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="stripe-mode-refresh"
          onClick={onRefresh}
          className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs underline"
        >
          Refresh
        </button>
      </div>
      <p className="text-[#A3A3A3] text-[11px]">
        Source: <span className="text-white">{status.key_source === 'environment' ? 'Environment variable (STRIPE_API_KEY)' : 'Missing — no key bound to this container'}</span>
        {status.host && (<> · Host: <span className="text-white">{status.host}</span></>)}
      </p>

      {/* iter105 — Webhook health (most common cause of "invoice didn't auto-mark paid"). */}
      <div
        data-testid="stripe-webhook-status"
        className={`mt-1 rounded px-2 py-1.5 text-[11px] border ${
          status.webhook_secret_set
            ? 'border-green-700/40 bg-green-500/5 text-green-200'
            : 'border-red-700/60 bg-red-500/10 text-red-200'
        }`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold uppercase tracking-wider text-[10px]">Webhook</span>
          <span>{status.webhook_secret_set ? '✓ STRIPE_WEBHOOK_SECRET is bound on this container' : '⚠ STRIPE_WEBHOOK_SECRET is NOT set — auto-finalize will fall back to browser polling only'}</span>
        </div>
        {status.expected_webhook_url && (
          <p className="text-[#A3A3A3] mt-0.5">
            Expected endpoint on Stripe Dashboard: <span className="font-mono text-white break-all" data-testid="expected-webhook-url">{status.expected_webhook_url}</span>
          </p>
        )}
      </div>
      {status.warning && (
        <p data-testid="stripe-mode-warning" className={`text-xs leading-snug ${isTest ? 'text-amber-300' : 'text-red-300'}`}>
          ⚠ {status.warning}
        </p>
      )}
      {status.db_key_set && (
        <p className="text-[#525252] text-[11px] italic">
          Note: a Stripe secret is also stored in System Settings, but this stack only loads from the env var above. The DB value is informational only.
        </p>
      )}

      {/* iter101 — Definitive probe: creates a real Stripe Checkout session and shows its prefix. */}
      <div className="pt-2 border-t border-[#262626] mt-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[#A3A3A3] text-[11px]">
            Need to verify? Run a probe — creates a real $0.50 Stripe Checkout session and shows whether the response is <span className="font-mono text-white">cs_live_</span> or <span className="font-mono text-white">cs_test_</span>. No charge, session auto-expires.
          </p>
          <button
            type="button"
            data-testid="stripe-probe-btn"
            onClick={runProbe}
            disabled={probing}
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {probing ? 'Probing…' : 'Run Stripe mode probe'}
          </button>
        </div>

        {probe && (
          <div
            data-testid="stripe-probe-result"
            className={`mt-2 rounded-md border px-3 py-2 text-[11px] space-y-1 ${
              probe.error
                ? 'border-red-700 bg-red-500/10 text-red-200'
                : probe.mismatch
                ? 'border-red-700 bg-red-500/10 text-red-200'
                : probe.session_mode === 'live'
                ? 'border-green-700 bg-green-500/10 text-green-200'
                : 'border-amber-700 bg-amber-500/10 text-amber-200'
            }`}
          >
            {probe.error ? (
              <>
                <p className="font-bold uppercase tracking-wider text-[10px]">Probe failed</p>
                <p data-testid="stripe-probe-error" className="font-mono break-all">{probe.error}</p>
                {probe.env_key_prefix && (
                  <p>Env key: <span className="font-mono text-white">{probe.env_key_prefix}…</span> · {probe.env_key_length} chars</p>
                )}
              </>
            ) : (
              <>
                <p className="font-bold uppercase tracking-wider text-[10px]">
                  Probe result: {probe.session_mode === 'live' ? '✅ LIVE' : '⚠ TEST'}
                  {probe.mismatch && ' — MISMATCH'}
                </p>
                <p>Env key prefix: <span data-testid="probe-env-prefix" className="font-mono text-white">{probe.env_key_prefix}…</span> ({probe.env_key_length} chars, {probe.env_mode})</p>
                <p>Session id prefix: <span data-testid="probe-session-prefix" className="font-mono text-white">{probe.session_id_prefix}…</span> ({probe.session_mode})</p>
                {probe.session_id && (
                  <p>Full session id: <span className="font-mono text-white break-all">{probe.session_id}</span></p>
                )}
                {probe.mismatch && (
                  <p className="text-red-300 mt-1">
                    ⚠ The env var prefix and the session id disagree. This usually means the container was deployed with one key but the SDK is somehow using another — please contact Emergent Support with the values above.
                  </p>
                )}
                {probe.checkout_url && (
                  <a
                    href={probe.checkout_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="stripe-probe-checkout-url"
                    className="text-[#D4AF37] hover:text-[#F3E5AB] underline"
                  >
                    Open the test session on Stripe →
                  </a>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// iter100 — Honest per-provider "where the running backend reads keys from" banner.
// Truth is hardcoded against server.py — see SOURCE_TRUTH inside ApiKeysForm.
const ProviderSourceBanner = ({ provider, truth, stripeStatus }) => {
  if (!truth) return null;
  // iter102 — When the env-only Stripe key is in live mode, use a neutral grey tone
  // instead of amber so the page doesn't read as "test mode". Amber is now reserved
  // for "env-only + actually in test mode" which is the genuine warning condition.
  const stripeIsLive = provider === 'stripe' && stripeStatus?.mode === 'live';
  const tone =
    truth.mode === 'env_only' && stripeIsLive ? { border: 'border-[#262626]',     bg: 'bg-[#0F0F0F]',     chip: 'text-[#A3A3A3] border-[#262626]' } :
    truth.mode === 'env_only'                 ? { border: 'border-amber-700/60',  bg: 'bg-amber-500/5',   chip: 'text-amber-300 border-amber-700' } :
    truth.mode === 'db_first_env_fallback'    ? { border: 'border-green-700/40',  bg: 'bg-green-500/5',   chip: 'text-green-300 border-green-700' } :
    truth.mode === 'stored_only'              ? { border: 'border-red-700/60',    bg: 'bg-red-500/5',     chip: 'text-red-300 border-red-700' } :
                                                { border: 'border-[#262626]',     bg: 'bg-[#0A0A0A]',     chip: 'text-[#A3A3A3] border-[#262626]' };

  return (
    <div
      data-testid={`provider-source-banner-${provider}`}
      className={`mb-3 rounded-md border ${tone.border} ${tone.bg} px-3 py-2 space-y-1`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tone.chip}`}>
          Active source
        </span>
        <span data-testid={`provider-source-label-${provider}`} className="text-white text-xs font-medium">
          {truth.label}
        </span>
        {provider === 'stripe' && stripeStatus?.mode === 'live' && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-green-700 text-green-300 ml-auto">
            Live
          </span>
        )}
        {provider === 'stripe' && stripeStatus?.mode === 'test' && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-amber-700 text-amber-300 ml-auto">
            Test
          </span>
        )}
      </div>
      <p className="text-[#A3A3A3] text-[11px] leading-snug">{truth.detail}</p>
      {provider === 'stripe' && (
        <p className="text-[#525252] text-[11px] italic">
          Where: Emergent Deployments → your app → Environment Variables → <span className="text-[#A3A3A3]">STRIPE_API_KEY</span>, <span className="text-[#A3A3A3]">STRIPE_WEBHOOK_SECRET</span> → Save → Redeploy.
        </p>
      )}
    </div>
  );
};



const ApiKeysForm = ({ value, onChange }) => {
  // iter79 — Email vendor renamed: Twilio Email (SendGrid under the hood) replaces Resend.


  // Legacy `resend` keys still shown if a tenant previously populated them.
  const providers = ['stripe', 'twilio_email', 'twilio', 'google_maps'];
  if ((value.resend || {}).api_key_set) providers.splice(2, 0, 'resend');
  const labelOf = (p) => (
    p === 'twilio_email' ? 'Email (Twilio / SendGrid)' :
    p === 'resend'       ? 'Resend (legacy — being phased out)' :
    p === 'twilio'       ? 'Twilio SMS' :
    p === 'google_maps'  ? 'Google Maps' :
    p === 'stripe'       ? 'Stripe' : p.replace('_', ' ')
  );

  // iter99 — Stripe mode visibility. Reads the backend's live container env so admins
  // know whether the deployed app is actually wired to live or test Stripe — separate
  // from the (mostly informational) System Settings inputs.
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeStatusLoading, setStripeStatusLoading] = useState(false);

  const loadStripeStatus = useCallback(async () => {
    setStripeStatusLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/integrations/stripe-status`, { withCredentials: true });
      setStripeStatus(data);
    } catch {
      setStripeStatus({ mode: 'unknown', warning: 'Failed to read Stripe status.' });
    }
    setStripeStatusLoading(false);
  }, []);

  useEffect(() => { loadStripeStatus(); }, [loadStripeStatus]);

  // iter100 — Per-provider truth about where the running backend ACTUALLY reads keys from.
  // This is determined by the backend code itself, not by what the admin types in here.
  // (Confirmed by grepping server.py.) DO NOT edit without updating server.py to match.
  const SOURCE_TRUTH = {
    stripe: {
      mode: 'env_only',
      label: 'Environment variable only',
      detail: 'Backend reads STRIPE_API_KEY from the deployed container. The fields below are NOT used on this deployment. Update production keys via the Emergent Deployment → Environment Variables screen and redeploy.',
      fields_active: false,
    },
    twilio_email: {
      mode: 'db_first_env_fallback',
      label: 'Database-first, environment variable fallback',
      detail: 'Backend prefers the API key saved here. Falls back to TWILIO_EMAIL_API_KEY / SENDGRID_API_KEY env vars if blank. Saving below takes effect immediately.',
      fields_active: true,
    },
    twilio: {
      mode: 'db_first_env_fallback',
      label: 'Database-first, environment variable fallback',
      detail: 'Backend prefers the credentials saved here. Falls back to TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN env vars if blank. Saving below takes effect immediately.',
      fields_active: true,
    },
    resend: {
      mode: 'db_first_env_fallback',
      label: 'Database-first, environment variable fallback (legacy)',
      detail: 'Resend is being phased out in favour of Twilio Email above. Saved values still work but new tenants should use Twilio Email.',
      fields_active: true,
    },
    google_maps: {
      mode: 'stored_only',
      label: 'Stored only — informational',
      detail: 'Backend code does not currently read a Google Maps key. Saving below stores the value for future use but it has no live effect today.',
      fields_active: false,
    },
  };

  return (
    <Section title="API Keys & Integrations" description="Secrets are write-only — once saved they are masked. Leave blank to keep the existing key.">
      <div className="space-y-4">
        {providers.map((p) => {
          const cfg = value[p] || {};
          return (
            <div key={p} data-testid={`api-provider-${p}`} className="border border-[#262626] rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-medium">{labelOf(p)}</p>
                <Badge className={cfg.status === 'configured' ? 'bg-green-500/20 text-green-400 border-green-700' : 'bg-[#262626] text-[#A3A3A3] border-[#262626]'}>
                  {cfg.status === 'configured' ? 'Configured' : 'Not configured'}
                </Badge>
              </div>

              {/* iter99 — Stripe live/test mode banner */}
              {p === 'stripe' && (
                <StripeModeBanner
                  status={stripeStatus}
                  loading={stripeStatusLoading}
                  onRefresh={loadStripeStatus}
                />
              )}

              {/* iter100 — Per-provider "active source" banner so admins know exactly
                  whether the inputs below are wired up or dead. */}
              {SOURCE_TRUTH[p] && (
                <ProviderSourceBanner
                  provider={p}
                  truth={SOURCE_TRUTH[p]}
                  stripeStatus={p === 'stripe' ? stripeStatus : null}
                />
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                {p === 'stripe' && (<>
                  <Field
                    label="Publishable Key (not used on this deployment)"
                    hint="This field is informational — production reads from env vars only."
                  >
                    <Input
                      data-testid="stripe-publishable-key"
                      value={cfg.publishable_key || ''}
                      onChange={(e) => onChange({ ...value, [p]: { ...cfg, publishable_key: e.target.value } })}
                      disabled
                      className="bg-[#0A0A0A] border-[#262626] text-[#525252] font-mono text-xs opacity-50 cursor-not-allowed"
                    />
                  </Field>
                  <Field
                    label="Secret Key (not used on this deployment)"
                    hint="Set STRIPE_API_KEY in the Emergent Deployment env vars instead."
                  >
                    <Input
                      data-testid="stripe-secret-key"
                      type="password"
                      placeholder={cfg.secret_key_set ? '••••••••' : 'sk_live_…'}
                      disabled
                      className="bg-[#0A0A0A] border-[#262626] text-[#525252] font-mono text-xs opacity-50 cursor-not-allowed"
                    />
                  </Field>
                  <Field
                    label="Webhook Secret (not used on this deployment)"
                    hint="Set STRIPE_WEBHOOK_SECRET in the Emergent Deployment env vars instead."
                  >
                    <Input
                      data-testid="stripe-webhook-secret"
                      type="password"
                      placeholder={cfg.webhook_secret_set ? '••••••••' : 'whsec_…'}
                      disabled
                      className="bg-[#0A0A0A] border-[#262626] text-[#525252] font-mono text-xs opacity-50 cursor-not-allowed"
                    />
                  </Field>
                </>)}
                {p === 'twilio_email' && (<>
                  <Field label="Verified From Email" hint="Must match a verified sender or domain in your Twilio Console (Communications APIs → Send Email)."><Input data-testid="twilio-email-from" value={cfg.from_email || ''} onChange={(e) => onChange({ ...value, [p]: { ...cfg, from_email: e.target.value } })} placeholder="noreply@nexflowplumbing.com" className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
                  <Field label={`API Key ${cfg.api_key_set ? '(set — leave blank to keep)' : ''}`} hint="Generate in Twilio Console → Communications APIs → Send Email → API Keys. Permission scope: Mail Send only.">
                    <Input data-testid="twilio-email-key" type="password" placeholder={cfg.api_key_set ? '••••••••' : 'SG.…'} onChange={(e) => onChange({ ...value, [p]: { ...cfg, api_key: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-xs" />
                  </Field>
                </>)}
                {p === 'resend' && (<>
                  <Field label="From Email"><Input value={cfg.from_email || ''} onChange={(e) => onChange({ ...value, [p]: { ...cfg, from_email: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
                  <Field label={`API Key ${cfg.api_key_set ? '(set — leave blank to keep)' : ''}`}><Input type="password" placeholder={cfg.api_key_set ? '••••••••' : 're_…'} onChange={(e) => onChange({ ...value, [p]: { ...cfg, api_key: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-xs" /></Field>
                </>)}
                {p === 'twilio' && (<>
                  <Field label="From Number (E.164)"><Input value={cfg.from_number || ''} onChange={(e) => onChange({ ...value, [p]: { ...cfg, from_number: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="+15551234567" /></Field>
                  <Field label={`Account SID ${cfg.account_sid_set ? '(set)' : ''}`}><Input type="password" placeholder={cfg.account_sid_set ? '••••••••' : 'AC…'} onChange={(e) => onChange({ ...value, [p]: { ...cfg, account_sid: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-xs" /></Field>
                  <Field label={`Auth Token ${cfg.auth_token_set ? '(set)' : ''}`}><Input type="password" placeholder={cfg.auth_token_set ? '••••••••' : '…'} onChange={(e) => onChange({ ...value, [p]: { ...cfg, auth_token: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-xs" /></Field>
                </>)}
                {p === 'google_maps' && (
                  <Field
                    label="API Key (not used on this deployment)"
                    hint="Backend has no Google Maps integration today — value is stored only."
                  >
                    <Input
                      data-testid="google-maps-key"
                      type="password"
                      placeholder={cfg.api_key_set ? '••••••••' : 'AIza…'}
                      disabled
                      className="bg-[#0A0A0A] border-[#262626] text-[#525252] font-mono text-xs opacity-50 cursor-not-allowed"
                    />
                  </Field>
                )}
              </div>
              {p === 'twilio_email' && (
                <div className="mt-4 pt-3 border-t border-[#262626]">
                  <EmailTestPanel currentFrom={cfg.from_email} />
                </div>
              )}
              {p === 'twilio_email' && (
                <p className="text-[#A3A3A3] text-xs mt-3">
                  iter79 — NexFlow uses <span className="text-[#D4AF37]">Twilio Email</span> for all transactional sends (password resets, portal invites, customer notifications). Twilio Email is built on SendGrid&apos;s infrastructure — the API key from your Twilio Console works directly with our SendGrid-compatible backend. Verify a sender domain in Twilio Console for the best deliverability.
                </p>
              )}
              {p !== 'twilio_email' && SOURCE_TRUTH[p]?.fields_active === true && (
                <p className="text-[#525252] text-xs mt-3">
                  Saving any field above stores it in <span className="text-[#A3A3A3]">system_settings.api_keys</span>. {SOURCE_TRUTH[p]?.detail}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ====== Tab definitions ======
const PmContactForm = ({ value, onChange }) => (
  <Section title="Property Manager Portal — Contact Card" description="Shown to Property Managers on their dashboard and Contacts page. Update anytime; no code change needed.">
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="General Phone"><Input data-testid="pm-contact-general-phone" value={value.general_phone || ''} onChange={(e) => onChange({ ...value, general_phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Emergency / After-Hours Phone"><Input data-testid="pm-contact-emergency-phone" value={value.emergency_phone || ''} onChange={(e) => onChange({ ...value, emergency_phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="General Email"><Input data-testid="pm-contact-general-email" value={value.general_email || ''} onChange={(e) => onChange({ ...value, general_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Dispatch Email"><Input data-testid="pm-contact-dispatch-email" value={value.dispatch_email || ''} onChange={(e) => onChange({ ...value, dispatch_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Billing Email"><Input data-testid="pm-contact-billing-email" value={value.billing_email || ''} onChange={(e) => onChange({ ...value, billing_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Website"><Input value={value.website || ''} onChange={(e) => onChange({ ...value, website: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      <Field label="Support Hours"><Input value={value.support_hours || ''} onChange={(e) => onChange({ ...value, support_hours: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
    </div>
    <Field label="Intro Blurb" hint="One short sentence shown above the contact buttons.">
      <Textarea rows={2} value={value.intro_blurb || ''} onChange={(e) => onChange({ ...value, intro_blurb: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
    </Field>
    <div>
      <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] mb-2">DEDICATED ACCOUNT MANAGER (OPTIONAL)</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Name"><Input value={value.account_manager_name || ''} onChange={(e) => onChange({ ...value, account_manager_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
        <Field label="Phone"><Input value={value.account_manager_phone || ''} onChange={(e) => onChange({ ...value, account_manager_phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
        <Field label="Email"><Input value={value.account_manager_email || ''} onChange={(e) => onChange({ ...value, account_manager_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></Field>
      </div>
    </div>
  </Section>
);

// iter75 Phase 5 — Export so AdminLayout can register these tabs in navIndex for pinnable Favorites.
// iter85 — `selfManaged: true` tabs own their own data + render (no shared settings doc/save bar).
export const SETTINGS_TABS = [
  { key: 'company',          label: 'Company',         icon: Building2, render: CompanyForm },
  { key: 'branding',         label: 'Branding',        icon: Palette,   render: BrandingForm },
  { key: 'memberships',      label: 'Memberships',     icon: Crown,     render: MembershipForm },
  { key: 'vip_payouts',      label: 'VIP Payouts',     icon: Gem,       render: VipPayoutsForm },
  { key: 'notifications',    label: 'Notifications',   icon: Bell,      render: SystemNotificationsForm },
  { key: 'invoice_defaults', label: 'Invoice Defaults', icon: FileText, render: InvoiceDefaultsForm },
  { key: 'promo_referral',   label: 'Promo/Referral',  icon: Tag,       render: PromoReferralForm },
  { key: 'dispatch',         label: 'Dispatch',        icon: MapPin,    render: DispatchForm },
  { key: 'holiday_calendar', label: 'Holidays',        icon: Calendar,  render: HolidayCalendarForm },
  { key: 'pm_contact',       label: 'PM Portal Contacts', icon: PhoneCall, render: PmContactForm },
  { key: 'warranty_companies', label: 'Warranty Companies', icon: ShieldCheck, render: WarrantyCompaniesForm, selfManaged: true },
  // iter89 — Diagnostics → Errors. Super-admin only (component itself enforces this).
  { key: 'diagnostics_errors', label: 'Diagnostics · Errors', icon: Bug, render: DiagnosticsErrorsForm, selfManaged: true, superAdminOnly: true },
  { key: 'api_keys',         label: 'API Keys',        icon: Key,       render: ApiKeysForm },
];
const TABS = SETTINGS_TABS;

const AdminSettings = () => {
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // iter75 Phase 5 — sync the active tab to ?tab=KEY so individual tabs are pinnable to Favorites.
  const [searchParams, setSearchParams] = useSearchParams();
  // iter89 — filter out super-admin-only tabs (Diagnostics) for everyone else.
  const visibleTabs = TABS.filter((t) => !t.superAdminOnly || user?.role === 'super_admin');
  const validKeys = visibleTabs.map((t) => t.key);
  const urlTab = searchParams.get('tab');
  const active = validKeys.includes(urlTab) ? urlTab : 'company';
  const setActive = (key) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'company') p.delete('tab'); else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  // iter75 Phase 5 — pin-to-Favorites for individual settings tabs.
  const [favorites, setFavorites] = useState([]);
  const refreshFavorites = useCallback(() => {
    axios.get(`${API}/user/favorites`, { withCredentials: true })
      .then((r) => setFavorites(r.data?.favorites || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    refreshFavorites();
    const onChange = () => refreshFavorites();
    window.addEventListener('nexflow:favorites-changed', onChange);
    return () => window.removeEventListener('nexflow:favorites-changed', onChange);
  }, [refreshFavorites]);
  const pathForTab = (key) => (key === 'company' ? '/admin/settings' : `/admin/settings?tab=${key}`);
  const togglePin = (key) => {
    const path = pathForTab(key);
    const next = favorites.includes(path) ? favorites.filter((p) => p !== path) : [...favorites, path];
    setFavorites(next);
    axios.put(`${API}/user/favorites`, { favorites: next }, { withCredentials: true })
      .then(() => window.dispatchEvent(new Event('nexflow:favorites-changed')))
      .catch(() => {});
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/settings`, { withCredentials: true });
        const mapped = {};
        Object.entries(data).forEach(([k, v]) => { mapped[k] = v.value; });
        setData(mapped);
        setOriginal(JSON.parse(JSON.stringify(mapped)));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load settings');
      }
      setLoading(false);
    })();
  }, []);

  const dirty = JSON.stringify(data[active] || {}) !== JSON.stringify(original[active] || {});

  const save = async () => {
    setSaving(true);
    try {
      const { data: resp } = await axios.put(`${API}/admin/settings/${active}`, { value: data[active] || {} }, { withCredentials: true });
      setData((prev) => ({ ...prev, [active]: resp.value }));
      setOriginal((prev) => ({ ...prev, [active]: JSON.parse(JSON.stringify(resp.value)) }));
      toast.success(`${resp.label} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const updateActive = (next) => {
    setData((prev) => ({ ...prev, [active]: next }));
  };

  return (
    <div data-testid="admin-settings-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><Shield className="w-6 h-6 text-[#D4AF37]" /> System Settings</h2>
        <p className="text-[#A3A3A3]">Company-wide configuration. Per-user preferences live on the <Link to="/profile" className="text-[#D4AF37] hover:text-[#F3E5AB]">My Profile</Link> page.</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Link to="/admin/permissions" className="px-2.5 py-1 rounded-md bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:text-white">Role Permissions →</Link>
          <Link to="/admin/reminders" className="px-2.5 py-1 rounded-md bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:text-white">Reminder Rules →</Link>
          <Link to="/admin/vip-settings" className="px-2.5 py-1 rounded-md bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:text-white">Per-VIP Overrides →</Link>
          <Link to="/admin/system" className="px-2.5 py-1 rounded-md bg-[#141414] border border-red-900 text-red-400 hover:text-red-300">Danger Zone →</Link>
        </div>
      </div>

      {/* iter95 — surface a loud warning when Public App URL is blank. This is the #1 cause
          of "View & Pay Invoice" links pointing to preview domains and returning Invalid
          payment link on production. */}
      {!loading && !(data.company && (data.company.public_app_url || '').trim()) && (
        <div data-testid="public-url-warning" className="bg-[#7F1D1D]/20 border border-[#7F1D1D] rounded-md p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-red-300 font-bold">Public App URL is not set — invoice payment links may break</p>
            <p className="text-[#A3A3A3] text-sm mt-1">
              Without this, every email payment link, QR code, and portal invite uses whatever host the request came from (often the internal preview URL).
              On production this must be your customer-facing domain (e.g. <span className="font-mono text-white">https://hub.nexflowplumbing.com</span>).
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button
                data-testid="public-url-set-current"
                onClick={() => {
                  const here = window.location.origin;
                  setData((prev) => ({ ...prev, company: { ...(prev.company || {}), public_app_url: here } }));
                  setActive('company');
                  toast.message(`Set to ${here} — click Save Changes to persist.`);
                }}
                className="h-9 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs font-bold"
              >
                Set to current domain ({(() => { try { return new URL(window.location.origin).host; } catch { return 'this site'; } })()})
              </Button>
              <Button
                data-testid="public-url-go-company"
                onClick={() => setActive('company')}
                variant="outline"
                className="h-9 border-[#262626] text-white hover:bg-[#262626] text-xs"
              >
                Fix in Company tab →
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#A3A3A3]">Loading…</div>
      ) : (
        <Tabs value={active} onValueChange={setActive} className="w-full">
          <div className="overflow-x-auto pb-1">
            <TabsList data-testid="settings-tabs" className="bg-[#141414] border border-[#262626] inline-flex h-auto whitespace-nowrap">
              {visibleTabs.map((t) => {
                const Icon = t.icon;
                const pinned = favorites.includes(pathForTab(t.key));
                return (
                  <TabsTrigger key={t.key} data-testid={`tab-${t.key}`} value={t.key} className="relative pr-7 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white shrink-0">
                    <Icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
                    <button
                      type="button"
                      data-testid={`settings-pin-${t.key}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(t.key); }}
                      title={pinned ? 'Unpin from Favorites' : 'Pin to Favorites'}
                      className={`absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded transition-colors ${
                        pinned ? 'text-[#D4AF37]' : 'text-[#525252] hover:text-[#D4AF37]'
                      } data-[state=active]:text-[#0A0A0A]`}
                    >
                      <Star className={`w-3 h-3 ${pinned ? 'fill-current' : ''}`} />
                    </button>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {visibleTabs.map((t) => {
            const Render = t.render;
            return (
              <TabsContent key={t.key} value={t.key} className="mt-6 space-y-6">
                {t.selfManaged ? (
                  <Render />
                ) : (
                  <>
                    <Render value={data[t.key] || {}} onChange={updateActive} />
                    <SaveBar onSave={save} saving={saving} dirty={dirty} />
                  </>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default AdminSettings;
